import { useEffect, useState, type ReactNode } from 'react';
import { NavLink } from 'react-router-dom';
import { ChevronDown, LogOut, Menu, ShieldAlert, X } from 'lucide-react';
import { useAuth } from '@proptr/api-client/react';
import { client, clearImpersonationToken } from '../main.js';
import { cn, initials as makeInitials } from '../lib/utils.js';
import { Button } from './ui/button.js';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu.js';
import { ThemeToggle } from './ThemeToggle.js';

const MANAGER_URL =
  (import.meta.env.VITE_MANAGER_URL as string | undefined) ??
  'http://localhost:5175';

const NAV: { to: string; label: string }[] = [
  { to: '/', label: 'Dashboard' },
  { to: '/inquiries', label: 'Inquiries' },
  { to: '/contacts', label: 'Contacts' },
  { to: '/proptree', label: 'My proptree' },
  { to: '/settings', label: 'Settings' },
];

// Common chrome shared by every authenticated agent page. Visual style
// mirrors the manager portal (same Tailwind tokens, same Topbar shape)
// so the agent app feels like part of the same product.
export function AppShell({ children }: { children: ReactNode }) {
  return (
    <div className="flex min-h-screen flex-col bg-background">
      <ImpersonationBanner />
      <Topbar />
      <main className="flex-1">
        <div className="mx-auto w-full max-w-[1400px] px-6 py-8">
          {children}
        </div>
      </main>
    </div>
  );
}

function Topbar() {
  const { user, logout } = useAuth();
  const [menuOpen, setMenuOpen] = useState(false);

  async function onLogout() {
    await logout();
    window.location.href = '/';
  }

  return (
    <>
      <header className="sticky top-0 z-30 border-b border-[hsl(var(--hairline))] bg-card">
        <div className="mx-auto flex h-16 w-full max-w-[1400px] items-center gap-3 px-4 md:gap-6 md:px-6">
          <Button
            variant="ghost"
            size="icon"
            className="md:hidden"
            onClick={() => setMenuOpen(true)}
            aria-label="Open menu"
          >
            <Menu />
          </Button>

          <span className="font-mono text-[12px] font-semibold uppercase tracking-[0.08em]">
            proptr.ee · agent
          </span>

          <nav className="hidden min-w-0 flex-1 items-center gap-1 md:flex">
            {NAV.map(({ to, label }) => (
              <NavLink
                key={to}
                to={to}
                end={to === '/'}
                className={({ isActive }) =>
                  cn(
                    'rounded-md px-3 py-1.5 font-sans text-[13px] font-medium transition-colors',
                    isActive
                      ? 'bg-muted text-foreground'
                      : 'text-muted-foreground hover:text-foreground',
                  )
                }
              >
                {label}
              </NavLink>
            ))}
          </nav>

          <div className="ml-auto flex shrink-0 items-center gap-1">
            <ThemeToggle />
            <div className="mx-1 hidden h-5 w-px bg-[hsl(var(--hairline))] md:block" />
            {user && <ProfileMenu user={user} onLogout={onLogout} />}
          </div>
        </div>
      </header>

      <MobileNavDrawer open={menuOpen} onClose={() => setMenuOpen(false)} />
    </>
  );
}

function MobileNavDrawer({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener('keydown', onKey);
    };
  }, [open, onClose]);

  return (
    <div
      className={cn(
        'fixed inset-0 z-50 md:hidden',
        open ? 'pointer-events-auto' : 'pointer-events-none',
      )}
      aria-hidden={!open}
    >
      <div
        onClick={onClose}
        className={cn(
          'absolute inset-0 bg-background/70 backdrop-blur-sm transition-opacity',
          open ? 'opacity-100' : 'opacity-0',
        )}
      />
      <aside
        className={cn(
          'absolute inset-y-0 left-0 flex w-[280px] flex-col border-r border-[hsl(var(--hairline))] bg-card shadow-pop transition-transform duration-300 ease-out',
          open ? 'translate-x-0' : '-translate-x-full',
        )}
      >
        <div className="flex h-16 items-center justify-between border-b border-[hsl(var(--hairline))] px-4">
          <span className="font-mono text-[12px] font-semibold uppercase tracking-[0.08em]">
            proptr.ee · agent
          </span>
          <Button
            variant="ghost"
            size="icon"
            onClick={onClose}
            aria-label="Close menu"
          >
            <X />
          </Button>
        </div>

        <nav className="flex flex-1 flex-col gap-0.5 p-3">
          {NAV.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === '/'}
              onClick={onClose}
              className={({ isActive }) =>
                cn(
                  'relative flex items-center gap-2.5 rounded-md px-3 py-2.5 font-sans text-[14px] font-medium transition-colors',
                  isActive
                    ? 'bg-primary/10 text-foreground'
                    : 'text-muted-foreground hover:bg-muted hover:text-foreground',
                )
              }
            >
              {({ isActive }) => (
                <>
                  {isActive && (
                    <span className="absolute inset-y-2 left-0 w-[2px] rounded-full bg-primary" />
                  )}
                  <span>{item.label}</span>
                </>
              )}
            </NavLink>
          ))}
        </nav>
      </aside>
    </div>
  );
}

function ProfileMenu({
  user,
  onLogout,
}: {
  user: { name?: string | null; email?: string | null; role?: string | null };
  onLogout: () => void | Promise<void>;
}) {
  const displayName = user.name ?? user.email?.split('@')[0] ?? 'Agent';
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          className="flex items-center gap-2.5 rounded-md py-1 pl-1 pr-2 outline-none ring-offset-background transition-colors hover:bg-muted focus-visible:ring-2 focus-visible:ring-ring md:pr-3"
          aria-label="Account menu"
        >
          <div className="flex h-8 w-8 items-center justify-center rounded-full border border-border bg-card font-mono text-[11px] font-bold uppercase text-foreground">
            {makeInitials(user.name ?? user.email ?? 'U')}
          </div>
          <div className="hidden text-left md:block">
            <div className="font-sans text-[12px] font-semibold leading-none">
              {displayName}
            </div>
            <div className="mt-1 font-mono text-[10px] uppercase tracking-[0.08em] text-muted-foreground leading-none">
              Agent
            </div>
          </div>
          <ChevronDown className="hidden h-3.5 w-3.5 text-muted-foreground md:block" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-60">
        <DropdownMenuLabel>
          <div className="font-semibold normal-case text-foreground">
            {displayName}
          </div>
          {user.email && (
            <div className="text-[11px] font-normal normal-case text-muted-foreground">
              {user.email}
            </div>
          )}
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          onSelect={() => {
            void onLogout();
          }}
        >
          <LogOut />
          Sign out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

// Sticky strip at the top, only when the active session was minted via
// /api/auth/manager/login-as-agent. Mirrors the manager portal's
// ImpersonationBanner. "Exit" re-issues the original manager's cookie
// and bounces back to the manager portal.
function ImpersonationBanner() {
  const { user, impersonator } = useAuth();
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  if (!user || !impersonator) return null;

  const agentLabel = user.name ?? user.email;
  const managerLabel = impersonator.name ?? impersonator.email;

  const handleExit = async () => {
    if (busy) return;
    setBusy(true);
    setErr(null);
    try {
      await client.auth.managerExitImpersonation();
      clearImpersonationToken();
      window.location.href = MANAGER_URL;
    } catch (e) {
      setErr((e as Error).message);
      setBusy(false);
    }
  };

  return (
    <div className="border-b border-amber-300/70 bg-amber-100 text-amber-950 dark:border-amber-500/30 dark:bg-amber-950/40 dark:text-amber-100">
      <div className="mx-auto flex w-full max-w-[1400px] items-center gap-3 px-6 py-2 text-[13px]">
        <ShieldAlert className="h-4 w-4 shrink-0" aria-hidden />
        <div className="min-w-0 flex-1 truncate">
          Logged in as <strong className="font-semibold">{agentLabel}</strong>
          <span className="mx-2 opacity-60">·</span>
          originally{' '}
          <strong className="font-semibold">{managerLabel}</strong>
          {err && (
            <span className="ml-3 font-mono text-[11px] text-destructive">
              {err}
            </span>
          )}
        </div>
        <button
          type="button"
          onClick={() => void handleExit()}
          disabled={busy}
          className="inline-flex shrink-0 items-center gap-1.5 rounded-md border border-amber-400/70 bg-white/70 px-3 py-1 font-mono text-[11px] font-medium uppercase tracking-[0.04em] text-amber-950 transition-colors hover:bg-white disabled:cursor-not-allowed disabled:opacity-60 dark:border-amber-500/40 dark:bg-amber-900/40 dark:text-amber-100 dark:hover:bg-amber-900/60"
        >
          <LogOut className="h-3.5 w-3.5" />
          {busy ? 'Exiting…' : 'Exit impersonation'}
        </button>
      </div>
    </div>
  );
}

// Reusable empty-state body for placeholder pages (Inquiries, Contacts,
// My proptree until those phases ship). Matches the manager's eyebrow +
// display-heading typography so the agent portal feels coherent.
export function PlaceholderPage({
  eyebrow,
  heading,
  body,
}: {
  eyebrow: string;
  heading: string;
  body: ReactNode;
}) {
  return (
    <div className="space-y-8 pb-10">
      <div className="border-b border-[hsl(var(--hairline))] pb-8">
        <div className="eyebrow mb-3">{eyebrow}</div>
        <h1 className="font-display text-[40px] font-medium leading-none tracking-[-0.025em]">
          {heading}
        </h1>
      </div>
      <div className="rounded-lg border border-dashed border-[hsl(var(--hairline))] bg-card p-10 text-center">
        <div className="mx-auto max-w-md font-mono text-[12px] leading-relaxed text-muted-foreground">
          {body}
        </div>
      </div>
    </div>
  );
}
